import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { fmtTRY, fmtDate, todayISO } from "@/lib/format";
import { toast } from "sonner";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { registerTurkishFont } from "@/lib/pdfFont";
import { COMPANY_NAME } from "@/lib/company";
import ChequeImportModal from "@/components/ChequeImportModal";
import { TR_BANKS, BANK_DATALIST_ID } from "@/lib/banks";
import { Link } from "react-router-dom";
import {
  Plus, Trash2, Edit3, X, Send, CheckCircle2, XCircle,
  Ban, Wallet, AlertTriangle, Landmark, ArrowLeftRight, Repeat, FileText, Upload,
  Search, ArrowUp, ArrowDown, ArrowUpDown, Image as ImageIcon,
} from "lucide-react";
import ChequeImagesModal from "@/components/ChequeImagesModal";

async function loadLogoDataUrl() {
  try {
    const res = await fetch("/plasper-logo.jpg");
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onloadend = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(blob);
    });
  } catch { return null; }
}

const DIRECTIONS = { received: "Alınan Çek", issued: "Verilen Çek" };
const STATUS = {
  portfolio: { label: "Portföyde", color: "bg-neutral-100 text-neutral-800 border-neutral-300" },
  in_bank: { label: "Tahsile Verildi", color: "bg-amber-50 text-amber-800 border-amber-300" },
  cleared: { label: "Tahsil Edildi", color: "bg-emerald-50 text-emerald-800 border-emerald-300" },
  endorsed: { label: "Ciro Edildi", color: "bg-indigo-50 text-indigo-800 border-indigo-300" },
  bounced: { label: "Karşılıksız", color: "bg-rose-50 text-rose-800 border-rose-300" },
  returned: { label: "İade Edildi", color: "bg-rose-50 text-rose-800 border-rose-300" },
  cancelled: { label: "İptal", color: "bg-neutral-50 text-neutral-500 border-neutral-200 line-through" },
};

const STATUS_ORDER = { portfolio: 0, in_bank: 1, cleared: 2, endorsed: 3, bounced: 4, returned: 5, cancelled: 6 };

export default function Cheques() {
  const [items, setItems] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [books, setBooks] = useState([]);
  const [tab, setTab] = useState("received");
  const [statusFilter, setStatusFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [statusModal, setStatusModal] = useState(null);
  const [imagesModal, setImagesModal] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [importOpen, setImportOpen] = useState(false);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ field: "due_date", dir: "asc" });

  const load = useCallback(async () => {
    const [{ data: ch }, { data: co }, { data: ac }, { data: sm }, booksRes] = await Promise.all([
      api.get("/cheques"),
      api.get("/contacts"),
      api.get("/accounts"),
      api.get("/cheques/summary"),
      api.get("/cheque-books").catch(() => ({ data: [] })),
    ]);
    setItems(ch); setContacts(co); setAccounts(ac); setSummary(sm);
    setBooks(booksRes.data || []);
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSelectedIds(new Set()); }, [tab]);

  const contactNames = useMemo(
    () => Object.fromEntries(contacts.map((c) => [c.id, c.name])), [contacts]
  );

  // Verilen çekler için koçan eşleşme haritası: bank_name (locale-lower) → ordered books
  // Her koçana "#N" indexi verilir (bankaya göre başlangıç numarasına göre sıralı).
  const bookIndex = useMemo(() => {
    const trLower = (s) => String(s || "").trim().toLocaleLowerCase("tr");
    const byBank = new Map();
    books.forEach((b) => {
      const k = trLower(b.bank_name);
      if (!byBank.has(k)) byBank.set(k, []);
      byBank.get(k).push(b);
    });
    byBank.forEach((arr) => arr.sort((a, b) => a.start_no - b.start_no));
    return byBank;
  }, [books]);

  const bookForCheque = useCallback((c) => {
    if (!c || c.direction !== "issued") return null;
    const trLower = (s) => String(s || "").trim().toLocaleLowerCase("tr");
    const list = bookIndex.get(trLower(c.bank_name));
    if (!list || !list.length) return null;
    const num = parseInt(String(c.cheque_number || "").trim(), 10);
    if (isNaN(num)) return null;
    for (let i = 0; i < list.length; i++) {
      const b = list[i];
      if (num >= b.start_no && num <= b.end_no) return { ...b, index: i + 1 };
    }
    return null;
  }, [bookIndex]);

  const toggleSort = (field) => {
    setSort((prev) => {
      if (prev.field !== field) return { field, dir: field === "amount" ? "desc" : "asc" };
      return { field, dir: prev.dir === "asc" ? "desc" : "asc" };
    });
  };

  const filtered = useMemo(() => {
    const query = q.trim().toLocaleLowerCase("tr");
    let arr = items.filter((c) =>
      c.direction === tab && (statusFilter === "all" || c.status === statusFilter)
    );
    if (query) {
      arr = arr.filter((c) => {
        const cname = (contactNames[c.contact_id] || "").toLocaleLowerCase("tr");
        const endName = (contactNames[c.endorsed_to_contact_id] || "").toLocaleLowerCase("tr");
        const num = String(c.cheque_number || "").toLocaleLowerCase("tr");
        const bank = String(c.bank_name || "").toLocaleLowerCase("tr");
        const branch = String(c.branch || "").toLocaleLowerCase("tr");
        return cname.includes(query) || endName.includes(query) ||
               num.includes(query) || bank.includes(query) || branch.includes(query);
      });
    }
    const getKey = (c) => {
      switch (sort.field) {
        case "cheque_number": return String(c.cheque_number || "").toLocaleLowerCase("tr");
        case "bank": return `${c.bank_name || ""} ${c.branch || ""}`.toLocaleLowerCase("tr");
        case "contact": return (contactNames[c.contact_id] || "").toLocaleLowerCase("tr");
        case "status": return STATUS_ORDER[c.status] ?? 99;
        case "amount": return Number(c.amount) || 0;
        default: return c.due_date || "";
      }
    };
    return [...arr].sort((a, b) => {
      const av = getKey(a), bv = getKey(b);
      const cmp = typeof av === "number" && typeof bv === "number"
        ? av - bv : String(av).localeCompare(String(bv), "tr");
      return sort.dir === "asc" ? cmp : -cmp;
    });
  }, [items, tab, statusFilter, q, sort, contactNames]);

  const toggleSel = (id) => {
    setSelectedIds((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  };
  const toggleAll = () => {
    setSelectedIds((prev) => {
      if (filtered.every((c) => prev.has(c.id))) return new Set();
      const s = new Set(prev);
      filtered.forEach((c) => s.add(c.id));
      return s;
    });
  };
  const selectedCheques = filtered.filter((c) => selectedIds.has(c.id));

  const generateBordro = async () => {
    if (selectedCheques.length === 0) { toast.error("En az bir çek seçin"); return; }
    const isReceivedTab = tab === "received";
    const endorsedCount = isReceivedTab ? selectedCheques.filter((c) => c.status === "endorsed").length : 0;
    if (endorsedCount && endorsedCount !== selectedCheques.length) {
      toast.error("Ciro edilmiş ve edilmemiş çekler aynı bordroda olamaz");
      return;
    }
    const isEndorsed = endorsedCount > 0;
    // Ciro edilen çeklerde karşı taraf, ciro edilen caridir (biz teslim ederiz)
    const partyId = (c) => (isEndorsed ? c.endorsed_to_contact_id : c.contact_id);
    const doc = new jsPDF({ unit: "mm", format: "a4", compress: true });
    await registerTurkishFont(doc);
    const w = doc.internal.pageSize.getWidth();
    const now = new Date();
    const bordroNo = `BRD-${now.getTime().toString(36).toUpperCase()}`;
    // Karşı taraflar unique
    const contactIds = [...new Set(selectedCheques.map(partyId).filter(Boolean))];
    const singleContact = contactIds.length === 1 ? contacts.find((x) => x.id === contactIds[0]) : null;
    const isReceived = isReceivedTab && !isEndorsed;
    const nameOf = (id) => contacts.find((x) => x.id === id)?.name || "-";

    // Logo
    const logo = await loadLogoDataUrl();
    if (logo) {
      try { doc.addImage(logo, "JPEG", 14, 10, 45, 15, undefined, "FAST"); } catch {}
    }

    // Header
    doc.setFontSize(16); doc.setFont(undefined, "bold");
    doc.text("ÇEK TESLİM BORDROSU", w / 2, 18, { align: "center" });
    doc.setFontSize(9); doc.setFont(undefined, "normal");
    doc.text(`Bordro No: ${bordroNo}`, 14, 30);
    doc.text(`Tarih: ${fmtDate(todayISO())}`, w - 14, 30, { align: "right" });

    // Taraflar kutusu
    autoTable(doc, {
      startY: 36,
      body: [
        ["Teslim Eden", isReceived ? (singleContact?.name || "—") : COMPANY_NAME],
        ["Teslim Alan", isReceived ? COMPANY_NAME : (singleContact?.name || "—")],
        ["Yön", isEndorsed ? "Ciro Edilen Çekler" : isReceived ? "Alınan Çekler" : "Verilen Çekler"],
        ...(singleContact ? [] : [["Not", `${contactIds.length} farklı cari — detay için tabloya bakınız`]]),
      ],
      theme: "grid",
      styles: { font: "Roboto", fontSize: 9, cellPadding: 2 },
      columnStyles: { 0: { fontStyle: "bold", cellWidth: 40, fillColor: [245, 245, 245] } },
      margin: { left: 14, right: 14 },
    });

    // Çek listesi
    const body = selectedCheques
      .sort((a, b) => (a.due_date || "").localeCompare(b.due_date || ""))
      .map((c, idx) => [
        idx + 1,
        c.cheque_number || "",
        c.bank_name || "",
        c.branch || "-",
        fmtDate(c.due_date),
        isEndorsed ? nameOf(c.contact_id) : null,
        !singleContact ? nameOf(partyId(c)) : null,
        fmtTRY(c.amount),
      ].filter((x) => x !== null));
    const head = [["#", "Çek No", "Banka", "Şube", "Vade", ...(isEndorsed ? ["Alındığı Cari"] : []),
      ...(!singleContact ? [isEndorsed ? "Ciro Edilen" : "Cari"] : []), "Tutar"]];
    autoTable(doc, {
      head, body,
      startY: doc.lastAutoTable.finalY + 4,
      theme: "striped",
      headStyles: { font: "Roboto", fontStyle: "bold", fillColor: [17, 17, 17], textColor: 255, fontSize: 9 },
      styles: { font: "Roboto", fontSize: 9, cellPadding: 2 },
      columnStyles: { 0: { halign: "center", cellWidth: 10 }, [head[0].length - 1]: { halign: "right", fontStyle: "bold" } },
      margin: { left: 14, right: 14 },
    });

    // Toplam
    const total = selectedCheques.reduce((s, c) => s + (c.amount || 0), 0);
    const y = doc.lastAutoTable.finalY + 6;
    doc.setFont(undefined, "bold"); doc.setFontSize(11);
    doc.text(`Toplam Tutar: ${fmtTRY(total)} (${selectedCheques.length} adet)`, w - 14, y, { align: "right" });

    // İmza alanları — bizim tarafımıza firma adı otomatik gelir
    const sigY = Math.max(y + 30, 240);
    doc.setFont(undefined, "normal"); doc.setFontSize(9);
    doc.line(24, sigY, 84, sigY);
    doc.line(w - 84, sigY, w - 24, sigY);
    doc.text("Teslim Eden", 54, sigY + 5, { align: "center" });
    doc.text("Teslim Alan", w - 54, sigY + 5, { align: "center" });

    // Firma adı otomatik — çek verirken sol tarafta (biz teslim eden), çek alırken sağ tarafta (biz teslim alan)
    doc.setFontSize(7.5); doc.setFont(undefined, "bold"); doc.setTextColor(0);
    const companyX = isReceived ? (w - 54) : 54;
    // Firma adı iki satıra bölünür ki ünvan sığsın
    const companyLines = doc.splitTextToSize(COMPANY_NAME, 62);
    companyLines.forEach((ln, i) => {
      doc.text(ln, companyX, sigY + 10 + i * 3.2, { align: "center" });
    });
    doc.setFont(undefined, "normal"); doc.setFontSize(7); doc.setTextColor(120);
    doc.text("Kaşe / İmza", isReceived ? 54 : (w - 54), sigY + 10, { align: "center" });
    doc.setTextColor(0);

    // Footer
    doc.setFontSize(7); doc.setTextColor(150);
    doc.text(`Plasper Finans · ${now.toLocaleString("tr-TR")}`, w / 2, 290, { align: "center" });

    doc.save(`cek-bordrosu-${bordroNo}.pdf`);
    toast.success("Bordro oluşturuldu");
  };

  const remove = async (id) => {
    if (!window.confirm("Çek silinsin mi? Tahsil edilmişse ilgili işlem de silinir.")) return;
    await api.delete(`/cheques/${id}`);
    toast.success("Silindi"); load();
  };

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="label-caps">Alınan & Verilen Çekler</p>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-2">Çek Portföyü</h1>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="cheque-import-button" onClick={() => setImportOpen(true)}
                  className="brutal-btn bg-white text-black border border-black px-4 py-2.5 text-sm flex items-center gap-2 hover:bg-black hover:text-white transition-colors">
            <Upload size={16} /> Excel'den İçe Aktar
          </button>
          <button data-testid="cheque-add-button" onClick={() => { setEditing(null); setOpen(true); }}
                  className="brutal-btn bg-black text-white px-4 py-2.5 text-sm flex items-center gap-2">
            <Plus size={16} /> Yeni Çek
          </button>
        </div>
      </header>

      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
          <StatCard label="Portföyde (Alınan)" value={summary.total_received} tone="emerald" icon={Wallet} tid="stat-cheque-received" />
          <StatCard label="Portföyde (Verilen)" value={summary.total_issued} tone="rose" icon={Send} tid="stat-cheque-issued" />
          <StatCard label="30 Günde Vadesi Gelen" value={summary.received_due_30 - summary.issued_due_30} tone="black" icon={Landmark} tid="stat-cheque-due30" subtitle={`Al: ${fmtTRY(summary.received_due_30)} · Öd: ${fmtTRY(summary.issued_due_30)}`} />
          <StatCard label="Karşılıksız" value={summary.bounced_amount} tone="rose" icon={AlertTriangle} tid="stat-cheque-bounced" subtitle={`${summary.bounced_count} adet`} />
        </div>
      )}

      {summary?.buckets && (
        <div className="brutal-card overflow-hidden" data-testid="cheque-buckets-card">
          <div className="p-5 border-b border-black/10 flex items-center justify-between">
            <div>
              <div className="label-caps">Vade Dilimlerine Göre Çek Özeti</div>
              <div className="text-xs text-neutral-500 mt-1">
                Portföyde ve tahsile verilmiş çekler — bugünden itibaren gün bazlı dağılım
                {(summary.overdue_received_count > 0 || summary.overdue_issued_count > 0) && (
                  <span className="ml-2 text-rose-600">
                    · Vadesi geçmiş: Al {fmtTRY(summary.overdue_received_amount)} ({summary.overdue_received_count}) · Öd {fmtTRY(summary.overdue_issued_amount)} ({summary.overdue_issued_count})
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="cheque-buckets-table">
              <thead>
                <tr className="border-b border-black/10 bg-neutral-50">
                  <th className="text-left px-5 py-2.5 label-caps">Vade Dilimi</th>
                  <th className="text-right px-5 py-2.5 label-caps text-emerald-700">Alınan Adet</th>
                  <th className="text-right px-5 py-2.5 label-caps text-emerald-700">Alınan Tutar</th>
                  <th className="text-right px-5 py-2.5 label-caps text-rose-600">Verilen Adet</th>
                  <th className="text-right px-5 py-2.5 label-caps text-rose-600">Verilen Tutar</th>
                  <th className="text-right px-5 py-2.5 label-caps">Net Nakit Akışı</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const totalRec = summary.buckets.reduce((s, b) => s + (b.received_amount || 0), 0);
                  const totalIss = summary.buckets.reduce((s, b) => s + (b.issued_amount || 0), 0);
                  const totalRecC = summary.buckets.reduce((s, b) => s + (b.received_count || 0), 0);
                  const totalIssC = summary.buckets.reduce((s, b) => s + (b.issued_count || 0), 0);
                  return (
                    <>
                      {summary.buckets.map((b) => {
                        const net = (b.received_amount || 0) - (b.issued_amount || 0);
                        return (
                          <tr key={b.key} className="border-b border-black/5 hover:bg-neutral-50" data-testid={`cheque-bucket-${b.key}`}>
                            <td className="px-5 py-3 font-medium">{b.label}</td>
                            <td className="px-5 py-3 text-right font-mono-num">{b.received_count || 0}</td>
                            <td className={`px-5 py-3 text-right font-mono-num font-semibold ${b.received_amount > 0 ? "text-emerald-700" : "text-neutral-400"}`}>
                              {fmtTRY(b.received_amount || 0)}
                            </td>
                            <td className="px-5 py-3 text-right font-mono-num">{b.issued_count || 0}</td>
                            <td className={`px-5 py-3 text-right font-mono-num font-semibold ${b.issued_amount > 0 ? "text-rose-600" : "text-neutral-400"}`}>
                              {fmtTRY(b.issued_amount || 0)}
                            </td>
                            <td className={`px-5 py-3 text-right font-mono-num font-semibold ${net > 0 ? "text-emerald-700" : net < 0 ? "text-rose-600" : "text-neutral-500"}`}>
                              {net === 0 ? "—" : (net > 0 ? "+" : "") + fmtTRY(net)}
                            </td>
                          </tr>
                        );
                      })}
                      <tr className="border-t-2 border-black/80 bg-neutral-50 font-semibold">
                        <td className="px-5 py-3 uppercase text-xs tracking-widest">Toplam</td>
                        <td className="px-5 py-3 text-right font-mono-num">{totalRecC}</td>
                        <td className="px-5 py-3 text-right font-mono-num text-emerald-700" data-testid="cheque-buckets-total-received">
                          {fmtTRY(totalRec)}
                        </td>
                        <td className="px-5 py-3 text-right font-mono-num">{totalIssC}</td>
                        <td className="px-5 py-3 text-right font-mono-num text-rose-600" data-testid="cheque-buckets-total-issued">
                          {fmtTRY(totalIss)}
                        </td>
                        <td className={`px-5 py-3 text-right font-mono-num ${totalRec - totalIss > 0 ? "text-emerald-700" : totalRec - totalIss < 0 ? "text-rose-600" : ""}`} data-testid="cheque-buckets-total-net">
                          {(totalRec - totalIss) === 0 ? "—" : ((totalRec - totalIss) > 0 ? "+" : "") + fmtTRY(totalRec - totalIss)}
                        </td>
                      </tr>
                    </>
                  );
                })()}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2 items-center">
        <div className="flex border border-black">
          {["received", "issued"].map((k) => (
            <button
              key={k} data-testid={`cheque-tab-${k}`} onClick={() => setTab(k)}
              className={`px-4 py-2 text-sm uppercase tracking-widest ${tab === k ? "bg-black text-white" : "bg-white"}`}
            >
              {DIRECTIONS[k]}
            </button>
          ))}
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none" />
          <input
            data-testid="cheque-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cari, çek no, banka ara…"
            className="border border-black/20 pl-8 pr-8 py-1.5 text-sm outline-none focus:border-black w-64"
          />
          {q && (
            <button
              type="button"
              data-testid="cheque-search-clear"
              onClick={() => setQ("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-black"
              title="Temizle"
            >
              <X size={13} />
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-1.5 ml-2">
          {["all", ...Object.keys(STATUS)].map((s) => (
            <button
              key={s} data-testid={`cheque-status-filter-${s}`} onClick={() => setStatusFilter(s)}
              className={`text-[11px] uppercase tracking-widest px-2.5 py-1 border ${
                statusFilter === s ? "bg-black text-white border-black" : "border-black/15 text-neutral-600 hover:border-black/40"
              }`}
            >
              {s === "all" ? "Tümü" : STATUS[s].label}
            </button>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-[11px] text-neutral-500 tabular-nums" data-testid="cheque-count">
            {filtered.length} kayıt
          </span>
          {selectedIds.size > 0 && (
            <span className="text-xs text-neutral-500" data-testid="cheque-selection-count">
              · {selectedIds.size} seçili
            </span>
          )}
          <button
            data-testid="cheque-bordro-btn"
            onClick={generateBordro}
            disabled={selectedIds.size === 0}
            className="brutal-btn border border-black bg-white text-black px-3 py-1.5 text-xs uppercase tracking-widest flex items-center gap-1.5 hover:bg-black hover:text-white transition-colors disabled:opacity-40 disabled:hover:bg-white disabled:hover:text-black"
            title="Seçili çekler için PDF bordro üret"
          >
            <FileText size={13} /> Bordro Oluştur
          </button>
        </div>
      </div>

      <div className="brutal-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="cheque-table">
          <thead>
            <tr className="border-b-2 border-black/80">
              <th className="px-3 py-3 w-6">
                <input
                  type="checkbox"
                  data-testid="cheque-select-all"
                  checked={filtered.length > 0 && filtered.every((c) => selectedIds.has(c.id))}
                  onChange={toggleAll}
                  className="w-4 h-4 accent-black"
                  aria-label="Tümünü seç"
                />
              </th>
              <SortTh field="due_date" label="Vade" sort={sort} onToggle={toggleSort} tid="sort-cheque-due" />
              <SortTh field="cheque_number" label="Çek No" sort={sort} onToggle={toggleSort} tid="sort-cheque-number" />
              <SortTh field="bank" label="Banka" sort={sort} onToggle={toggleSort} tid="sort-cheque-bank" />
              <SortTh field="contact" label={tab === "received" ? "Alınan Cari" : "Verilen Cari"} sort={sort} onToggle={toggleSort} tid="sort-cheque-contact" />
              <SortTh field="status" label="Durum" sort={sort} onToggle={toggleSort} tid="sort-cheque-status" />
              <SortTh field="amount" label="Tutar" sort={sort} onToggle={toggleSort} align="right" tid="sort-cheque-amount" />
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="text-center py-10 text-neutral-500">
                {q ? "Arama kriterine uyan kayıt yok." : "Kayıt yok. Yeni çek ekleyin."}
              </td></tr>
            )}
            {filtered.map((c) => (
              <tr key={c.id} className={`border-b border-black/5 hover:bg-neutral-50 ${selectedIds.has(c.id) ? "bg-amber-50/50" : ""}`}>
                <td className="px-3 py-3">
                  <input
                    type="checkbox"
                    data-testid={`cheque-select-${c.id}`}
                    checked={selectedIds.has(c.id)}
                    onChange={() => toggleSel(c.id)}
                    className="w-4 h-4 accent-black"
                  />
                </td>
                <td className="px-4 py-3 font-mono-num">{fmtDate(c.due_date)}</td>
                <td className="px-4 py-3 font-mono-num">
                  {c.cheque_number}
                  {(() => {
                    const bk = bookForCheque(c);
                    return bk ? (
                      <Link
                        to={`/cek-kocanlari?highlight=${bk.id}`}
                        data-testid={`cheque-book-badge-${c.id}`}
                        className="inline-block ml-2 text-[10px] px-1.5 py-0.5 border border-indigo-300 bg-indigo-50 text-indigo-700 uppercase tracking-widest font-sans hover:bg-indigo-100 hover:border-indigo-500"
                        title={`Koçan: ${bk.bank_name} · ${bk.start_no}-${bk.end_no} — Tıklayarak koçan detayını aç`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        Koçan #{bk.index}
                      </Link>
                    ) : null;
                  })()}
                </td>
                <td className="px-4 py-3">{c.bank_name}{c.branch && <span className="text-xs text-neutral-500 block">{c.branch}</span>}</td>
                <td className="px-4 py-3 text-neutral-700">
                  {contacts.find(x => x.id === c.contact_id)?.name || "—"}
                  {c.status === "endorsed" && c.endorsed_to_contact_id && (
                    <div className="text-[11px] text-indigo-700 mt-0.5 flex items-center gap-1">
                      <Repeat size={11} /> Ciro: {contacts.find(x => x.id === c.endorsed_to_contact_id)?.name || "—"}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className={`border px-2 py-1 text-[11px] uppercase tracking-widest ${STATUS[c.status].color}`}>
                    {STATUS[c.status].label}
                  </span>
                </td>
                <td className={`px-4 py-3 text-right font-mono-num font-semibold ${c.direction === "received" ? "text-emerald-700" : "text-rose-600"}`}>
                  {fmtTRY(c.amount)}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {c.status !== "cleared" && c.status !== "cancelled" && (
                    <button data-testid={`cheque-status-btn-${c.id}`} onClick={() => setStatusModal(c)}
                            className="p-1.5 hover:bg-neutral-100" title="Durumu Güncelle">
                      <ArrowLeftRight size={14} />
                    </button>
                  )}
                  {(() => {
                    const n = Object.keys(c.images || {}).length;
                    return (
                      <button data-testid={`cheque-images-btn-${c.id}`} onClick={() => setImagesModal(c)}
                        className="p-1.5 hover:bg-neutral-100 relative" title={n ? `${n} görüntü` : "Görüntü ekle"}>
                        <ImageIcon size={14} className={n ? "text-sky-700" : "text-neutral-300"} />
                        {n > 0 && (
                          <span data-testid={`cheque-images-count-${c.id}`} className="absolute -top-0.5 -right-0.5 text-[9px] leading-none bg-sky-700 text-white px-1 py-0.5 font-mono-num">{n}</span>
                        )}
                      </button>
                    );
                  })()}
                  <button onClick={() => { setEditing(c); setOpen(true); }} className="p-1.5 hover:bg-neutral-100">
                    <Edit3 size={14} />
                  </button>
                  <button data-testid={`cheque-delete-${c.id}`} onClick={() => remove(c.id)} className="p-1.5 hover:bg-rose-50">
                    <Trash2 size={14} className="text-rose-600" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <ChequeModal
          editing={editing} contacts={contacts} accounts={accounts}
          defaultDirection={tab}
          onClose={() => setOpen(false)}
          onSaved={() => { setOpen(false); load(); }}
        />
      )}
      {statusModal && (
        <StatusModal
          cheque={statusModal} accounts={accounts} contacts={contacts}
          onClose={() => setStatusModal(null)}
          onSaved={() => { setStatusModal(null); load(); }}
        />
      )}
      {imagesModal && (
        <ChequeImagesModal
          cheque={imagesModal}
          onClose={() => setImagesModal(null)}
          onChanged={(upd) => setItems((prev) => prev.map((x) => (x.id === upd.id ? upd : x)))}
        />
      )}
      {importOpen && (
        <ChequeImportModal
          defaultDirection={tab}
          onClose={() => setImportOpen(false)}
          onImported={() => { load(); }}
        />
      )}
    </div>
  );
}

function StatCard({ label, value, tone, icon: Icon, tid, subtitle }) {
  const color = tone === "emerald" ? "text-emerald-700" : tone === "rose" ? "text-rose-600" : "";
  return (
    <div className="brutal-card p-5" data-testid={tid}>
      <div className="flex items-start justify-between">
        <span className="label-caps">{label}</span>
        <Icon size={16} className="text-neutral-500" />
      </div>
      <div className={`font-mono-num text-2xl md:text-3xl font-semibold mt-3 ${color}`}>{fmtTRY(value)}</div>
      {subtitle && <div className="text-[11px] text-neutral-500 mt-1">{subtitle}</div>}
    </div>
  );
}

function ChequeModal({ editing, contacts, accounts, defaultDirection, onClose, onSaved }) {
  const [form, setForm] = useState(editing || {
    direction: defaultDirection || "received",
    cheque_number: "",
    bank_name: "",
    branch: "",
    amount: "",
    issue_date: todayISO(),
    due_date: todayISO(),
    contact_id: "",
    account_id: "",
    status: "portfolio",
    notes: "",
  });
  const [suggestion, setSuggestion] = useState(null);

  // Verilen çek eklerken banka değişince koçandan sıradaki numarayı öner.
  useEffect(() => {
    if (editing) { setSuggestion(null); return; }
    if (form.direction !== "issued") { setSuggestion(null); return; }
    const bank = (form.bank_name || "").trim();
    if (!bank) { setSuggestion(null); return; }
    let cancelled = false;
    api.get(`/cheque-books/next-number?bank_name=${encodeURIComponent(bank)}`)
      .then(({ data }) => { if (!cancelled) setSuggestion(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [form.direction, form.bank_name, editing]);

  const save = async (e) => {
    e.preventDefault();
    const p = { ...form, amount: Number(form.amount) };
    try {
      if (editing) await api.put(`/cheques/${editing.id}`, p);
      else await api.post("/cheques", p);
      toast.success("Kaydedildi"); onSaved();
    } catch (err) {
      toast.error("Kaydedilemedi");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-white border border-black w-full max-w-lg max-h-[90vh] overflow-y-auto" data-testid="cheque-form-modal">
        <div className="flex items-center justify-between p-5 border-b-2 border-black">
          <h2 className="font-display text-xl font-bold">{editing ? "Çeki Düzenle" : "Yeni Çek"}</h2>
          <button type="button" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(DIRECTIONS).map(([k, l]) => (
              <button key={k} type="button" data-testid={`cheque-direction-${k}`}
                      onClick={() => setForm({ ...form, direction: k })}
                      className={`border py-2 text-sm uppercase tracking-widest ${
                        form.direction === k
                          ? (k === "received" ? "bg-emerald-700 text-white border-emerald-700" : "bg-rose-600 text-white border-rose-600")
                          : "border-black/20"
                      }`}>
                {l}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <F label="Çek No"><input required data-testid="cheque-number" value={form.cheque_number}
                                     onChange={(e) => setForm({ ...form, cheque_number: e.target.value })}
                                     className="input font-mono-num" placeholder="0000123" />
              {suggestion?.suggestion && !form.cheque_number && (
                <button
                  type="button"
                  data-testid="cheque-number-suggest"
                  onClick={() => setForm({ ...form, cheque_number: String(suggestion.suggestion) })}
                  className="text-[11px] text-emerald-700 hover:text-emerald-800 underline decoration-dotted underline-offset-2 mt-1"
                >
                  Koçandan öner: {suggestion.suggestion}
                </button>
              )}
              {suggestion && !suggestion.suggestion && suggestion.notes && (
                <p className="text-[11px] text-rose-600 mt-1">{suggestion.notes}</p>
              )}
            </F>
            <F label="Tutar (₺)"><input required type="number" step="0.01" data-testid="cheque-amount"
                                       value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
                                       className="input font-mono-num" /></F>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <F label="Banka"><input required data-testid="cheque-bank" value={form.bank_name}
                                    list={BANK_DATALIST_ID}
                                    autoComplete="off"
                                    onChange={(e) => setForm({ ...form, bank_name: e.target.value })}
                                    className="input" placeholder="Listeden seçin veya yazın" />
              <datalist id={BANK_DATALIST_ID}>
                {TR_BANKS.map((b) => <option key={b} value={b} />)}
              </datalist>
            </F>
            <F label="Şube"><input data-testid="cheque-branch" value={form.branch || ""}
                                  onChange={(e) => setForm({ ...form, branch: e.target.value })}
                                  className="input" placeholder="Merkez" /></F>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <F label="Düzenleme Tarihi"><input required type="date" data-testid="cheque-issue-date"
                                              value={form.issue_date}
                                              onChange={(e) => setForm({ ...form, issue_date: e.target.value })}
                                              className="input" /></F>
            <F label="Vade Tarihi"><input required type="date" data-testid="cheque-due-date"
                                        value={form.due_date}
                                        onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                                        className="input" /></F>
          </div>
          <F label={form.direction === "received" ? "Alındığı Cari" : "Verildiği Cari"}>
            <select data-testid="cheque-contact" value={form.contact_id || ""}
                    onChange={(e) => setForm({ ...form, contact_id: e.target.value })}
                    className="input bg-white">
              <option value="">—</option>
              {contacts.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </F>
          <F label="İlgili Hesap (opsiyonel — tahsilde kullanılacak)">
            <select data-testid="cheque-account" value={form.account_id || ""}
                    onChange={(e) => setForm({ ...form, account_id: e.target.value })}
                    className="input bg-white">
              <option value="">—</option>
              {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </F>
          <F label="Notlar">
            <textarea rows={2} data-testid="cheque-notes" value={form.notes || ""}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      className="input" />
          </F>
        </div>
        <div className="flex justify-end gap-2 p-5 border-t border-black/10 bg-neutral-50">
          <button type="button" onClick={onClose} className="border border-black/20 px-4 py-2 text-sm">Vazgeç</button>
          <button data-testid="cheque-save-button" type="submit" className="brutal-btn bg-black text-white px-5 py-2 text-sm">Kaydet</button>
        </div>
        <style>{`.input{width:100%;border:1px solid rgba(0,0,0,0.2);padding:0.5rem 0.75rem;outline:none}`}</style>
      </form>
    </div>
  );
}

function StatusModal({ cheque, accounts, contacts, onClose, onSaved }) {
  const [newStatus, setNewStatus] = useState("");
  const [accountId, setAccountId] = useState(cheque.account_id || "");
  const [endorsedTo, setEndorsedTo] = useState(cheque.endorsed_to_contact_id || "");
  const [endorsedDate, setEndorsedDate] = useState(cheque.endorsed_date || todayISO());

  const isAlreadyEndorsed = cheque.status === "endorsed";
  const baseOptions = [
    { key: "in_bank", label: "Tahsile Ver (Bankaya)", icon: Landmark, tone: "amber" },
    { key: "cleared", label: cheque.direction === "received" ? "Tahsil Edildi (Para Yattı)" : "Ödendi", icon: CheckCircle2, tone: "emerald" },
    ...(cheque.direction === "received"
      ? [{ key: "endorsed",
          label: isAlreadyEndorsed ? "Ciro Edilen Cariyi Değiştir" : "Ciro Edildi (Devredildi)",
          icon: Repeat, tone: "indigo" }]
      : []),
    { key: "bounced", label: "Karşılıksız", icon: XCircle, tone: "rose" },
    { key: "returned", label: "İade Edildi", icon: Ban, tone: "rose" },
    { key: "cancelled", label: "İptal Et", icon: Ban, tone: "gray" },
    { key: "portfolio", label: isAlreadyEndorsed ? "Cirodan Geri Al (Portföye)" : "Portföye Al", icon: Wallet, tone: "gray" },
  ];
  // endorsed durumundayken 'endorsed' option'ını göster (ciro değiştirmek için)
  const options = baseOptions.filter((o) => isAlreadyEndorsed ? o.key !== cheque.status || o.key === "endorsed" : o.key !== cheque.status);

  const apply = async () => {
    if (!newStatus) return;
    try {
      const payload = { status: newStatus };
      if (newStatus === "cleared") {
        if (!accountId) { toast.error("Tahsil hesabı seçin"); return; }
        payload.account_id = accountId;
      }
      if (newStatus === "endorsed") {
        if (!endorsedTo) { toast.error("Ciro edilen cari seçin"); return; }
        payload.endorsed_to_contact_id = endorsedTo;
        payload.endorsed_date = endorsedDate;
      }
      await api.post(`/cheques/${cheque.id}/status`, payload);
      toast.success("Durum güncellendi");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Hata");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white border border-black w-full max-w-md max-h-[90vh] overflow-y-auto" data-testid="cheque-status-modal">
        <div className="flex items-center justify-between p-5 border-b-2 border-black">
          <div>
            <div className="label-caps">Çek Durumu</div>
            <h2 className="font-display text-lg font-bold">{cheque.cheque_number} · {fmtTRY(cheque.amount)}</h2>
          </div>
          <button type="button" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-2">
          {options.map((o) => (
            <button key={o.key} data-testid={`cheque-set-status-${o.key}`}
                    onClick={() => setNewStatus(o.key)}
                    className={`w-full text-left border px-4 py-3 flex items-center gap-3 transition-colors ${
                      newStatus === o.key ? "border-black bg-neutral-100" : "border-black/15 hover:border-black/40"
                    }`}>
              <o.icon size={16} />
              <span className="text-sm font-medium">{o.label}</span>
            </button>
          ))}
          {newStatus === "cleared" && (
            <div className="pt-2">
              <div className="label-caps mb-1.5">Hangi hesaba işlensin?</div>
              <select data-testid="cheque-clear-account" value={accountId}
                      onChange={(e) => setAccountId(e.target.value)}
                      className="w-full border border-black/20 px-3 py-2 bg-white outline-none">
                <option value="">— Seçin —</option>
                {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
              <div className="text-[11px] text-neutral-500 mt-1.5">
                Tahsil edildiğinde otomatik bir {cheque.direction === "received" ? "gelir" : "gider"} işlemi oluşturulur.
              </div>
            </div>
          )}
          {newStatus === "endorsed" && (
            <div className="pt-2 space-y-2">
              <div>
                <div className="label-caps mb-1.5">Ciro Edilen Cari</div>
                <select data-testid="cheque-endorsed-contact" value={endorsedTo}
                        onChange={(e) => setEndorsedTo(e.target.value)}
                        className="w-full border border-black/20 px-3 py-2 bg-white outline-none">
                  <option value="">— Seçin —</option>
                  {contacts.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <div className="label-caps mb-1.5">Ciro Tarihi</div>
                <input type="date" data-testid="cheque-endorsed-date" value={endorsedDate}
                       onChange={(e) => setEndorsedDate(e.target.value)}
                       className="w-full border border-black/20 px-3 py-2 outline-none" />
              </div>
              <div className="text-[11px] text-neutral-500 border-l-2 border-indigo-500 pl-3 leading-relaxed">
                Ciro edilen çekler portföyünüzden çıkar; para hareketi oluşturulmaz. Karşı tarafa borcunuzu ödemek için verilmiş sayılır.
              </div>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 p-5 border-t border-black/10 bg-neutral-50">
          <button onClick={onClose} className="border border-black/20 px-4 py-2 text-sm">Vazgeç</button>
          <button data-testid="cheque-status-apply" onClick={apply} disabled={!newStatus}
                  className="brutal-btn bg-black text-white px-5 py-2 text-sm disabled:opacity-40">
            Uygula
          </button>
        </div>
      </div>
    </div>
  );
}

function F({ label, children }) {
  return <label className="block"><div className="label-caps mb-1.5">{label}</div>{children}</label>;
}

function SortTh({ field, label, sort, onToggle, align, tid }) {
  const isActive = sort.field === field;
  const Icon = !isActive ? ArrowUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <th className={`px-4 py-3 ${align === "right" ? "text-right" : "text-left"}`}>
      <button type="button" data-testid={tid} onClick={() => onToggle(field)}
        className={`label-caps inline-flex items-center gap-1 select-none ${isActive ? "text-black" : "text-neutral-500 hover:text-black"} ${align === "right" ? "flex-row-reverse" : ""}`}>
        <span>{label}</span>
        <Icon size={11} strokeWidth={2.2} className={isActive ? "opacity-100" : "opacity-50"} />
      </button>
    </th>
  );
}
